import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState, useEffect } from "react";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useAuth } from "@/contexts/AuthContext";
import {
  useArchives,
  MODE_OPTIONS,
  type ArchiveStatus,
} from "@/hooks/use-archives";
import {
  NanaeHeader,
  NanaeFooter,
  QSectionHeader,
  SaisineCard,
  type SaisineCardData,
  type SaisineStatus,
} from "@/components/qv3";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/archives")({
  head: () => ({
    meta: [
      { title: "Le Greffe · Hōjin" },
      {
        name: "description",
        content:
          "Le Greffe — registre de toutes vos saisines : recherche, filtres et accès aux mémos rendus par le Conseil.",
      },
      { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Le Greffe · Hōjin",
        description: "Le Greffe — registre de toutes vos saisines : recherche, filtres et accès aux mémos rendus par le Conseil.",
        url: "https://hojin.ai/archives",
        image: "secretaire",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/archives" }],
  }),
  component: () => (
    <ProtectedRoute>
      <ArchivesPage />
    </ProtectedRoute>
  ),
  errorComponent: ({ error }) => (
    <div className="nanae-page qv3-dark" style={{ padding: 48 }}>
      <p className="qv3-mono-hud is-caps">Erreur · {error.message}</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="nanae-page qv3-dark" style={{ padding: 48 }}>
      <p className="qv3-mono-hud is-caps">Greffe introuvable</p>
    </div>
  ),
});

const STATUS_LABEL: Record<ArchiveStatus, string> = {
  completed: "Verdict rendu",
  active: "En débat",
  maieutique: "Antichambre",
  failed: "Interrompue",
  archived: "Archivée",
};

function ArchivesPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { dossiers, allDossiers, totalCount, totalFiltered, filters, setFilter, setPage, loading } =
    useArchives();
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);

  const suggestions = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return [];
    return allDossiers
      .filter((d) => `${d.question} ${d.verdictSynthesis}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [allDossiers, filters.search]);

  useEffect(() => {
    if (!suggestOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) setSuggestOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [suggestOpen]);

  useEffect(() => {
    setActiveIdx(-1);
  }, [filters.search]);

  const goToDossier = (id: string) => {
    setSuggestOpen(false);
    void navigate({ to: "/deliberation/$id", params: { id } });
  };


  const totalPages = Math.max(1, Math.ceil(totalFiltered / filters.pageSize));
  const from = totalFiltered === 0 ? 0 : (filters.page - 1) * filters.pageSize + 1;
  const to = Math.min(filters.page * filters.pageSize, totalFiltered);

  const cards = useMemo<SaisineCardData[]>(
    () =>
      dossiers.map((d) => ({
        id: d.id,
        title: d.question,
        date: d.createdAt ? new Date(d.createdAt).toISOString() : null,
        status: d.status as SaisineStatus,
        mode: typeof d.mode === "string" ? d.mode : null,
      })),
    [dossiers],
  );

  const anyFilter =
    filters.search.trim() !== "" ||
    filters.mode !== "all" ||
    filters.status !== "all" ||
    filters.period !== "all";

  const pageButtons = buildPageList(filters.page, totalPages);

  return (
    <div className="nanae-page qv3-dark">
      <NanaeHeader
        title="Le Greffe"
        user={{
          email: user?.email,
          name:
            (user?.user_metadata as { first_name?: string } | undefined)?.first_name ??
            user?.email,
        }}
        rightSlot={
          <button
            type="button"
            onClick={() => void signOut()}
            className="qv3-btn-link"
            style={{ color: "inherit" }}
          >
            Déconnexion
          </button>
        }
      />

      <main className="nanae-page-main">
        {/* § I — Registre */}
        <section>
          <QSectionHeader
            number="I"
            title="REGISTRE DES SAISINES"
            hint={
              loading
                ? "Chargement du Greffe…"
                : `${totalCount} dossier${totalCount > 1 ? "s" : ""} consigné${totalCount > 1 ? "s" : ""}.`
            }
          />

          {/* Toolbar : recherche + filtres */}
          <div className="greffe-toolbar" role="search">
            <div className="greffe-search" ref={searchWrapRef}>
              <span aria-hidden="true" className="greffe-search-icon">⌕</span>
              <input
                type="search"
                className="qv3-input-lcd"
                value={filters.search}
                onChange={(e) => {
                  setFilter("search", e.target.value);
                  setSuggestOpen(true);
                }}
                onFocus={() => setSuggestOpen(true)}
                onKeyDown={(e) => {
                  if (!suggestOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
                    setSuggestOpen(true);
                    return;
                  }
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveIdx((i) => Math.max(0, i - 1));
                  } else if (e.key === "Enter" && activeIdx >= 0 && suggestions[activeIdx]) {
                    e.preventDefault();
                    goToDossier(suggestions[activeIdx].id);
                  } else if (e.key === "Escape") {
                    setSuggestOpen(false);
                  }
                }}
                placeholder="Rechercher par question ou verdict…"
                aria-label="Rechercher dans le Greffe"
                aria-autocomplete="list"
                aria-expanded={suggestOpen && suggestions.length > 0}
                aria-controls="greffe-suggest"
              />
              {suggestOpen && suggestions.length > 0 && (
                <ul
                  id="greffe-suggest"
                  role="listbox"
                  className="greffe-suggest"
                  aria-label="Suggestions de saisines"
                >
                  {suggestions.map((s, i) => (
                    <li
                      key={s.id}
                      role="option"
                      aria-selected={i === activeIdx}
                      className={`greffe-suggest-item${i === activeIdx ? " is-active" : ""}`}
                      onMouseEnter={() => setActiveIdx(i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        goToDossier(s.id);
                      }}
                    >
                      <span className="greffe-suggest-num">{s.saisineNumber}</span>
                      <span className="greffe-suggest-title">{s.question}</span>
                      <span className="greffe-suggest-meta">
                        {STATUS_LABEL[s.status]} · {s.date}
                      </span>
                    </li>
                  ))}
                  <li className="greffe-suggest-foot" aria-hidden="true">
                    ↵ ouvrir · ↑↓ naviguer · esc fermer
                  </li>
                </ul>
              )}
            </div>

            <div className="greffe-filters">
              <select
                className="qv3-select-lcd"
                value={filters.mode}
                onChange={(e) => setFilter("mode", e.target.value)}
                aria-label="Filtrer par mode"
              >
                <option value="all">Tous modes</option>
                {MODE_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="qv3-select-lcd"
                value={filters.status}
                onChange={(e) => setFilter("status", e.target.value)}
                aria-label="Filtrer par statut"
              >
                <option value="all">Tout statut</option>
                {(Object.keys(STATUS_LABEL) as ArchiveStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
              <select
                className="qv3-select-lcd"
                value={filters.period}
                onChange={(e) => setFilter("period", e.target.value)}
                aria-label="Filtrer par période"
              >
                <option value="all">Toute période</option>
                <option value="7d">7 derniers jours</option>
                <option value="30d">30 derniers jours</option>
                <option value="3m">3 derniers mois</option>
                <option value="year">Année MMXXVI</option>
              </select>
            </div>
          </div>

          <div className="greffe-count">
            <strong>{totalFiltered}</strong>
            <span>dossier{totalFiltered > 1 ? "s" : ""} affiché{totalFiltered > 1 ? "s" : ""}</span>
            {anyFilter && (
              <>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  className="qv3-btn-link"
                  style={{ color: "inherit" }}
                  onClick={() => {
                    setFilter("search", "");
                    setFilter("mode", "all");
                    setFilter("status", "all");
                    setFilter("period", "all");
                  }}
                >
                  Réinitialiser
                </button>
              </>
            )}
          </div>

          {/* Liste */}
          <div role="list" aria-label="Saisines">
            {cards.length === 0 ? (
              <div className="greffe-empty">
                {anyFilter
                  ? "Aucun dossier ne correspond à ces filtres."
                  : loading
                    ? "Ouverture du Greffe…"
                    : "Aucune saisine au Greffe. Présentez la première au Conseil."}
              </div>
            ) : (
              cards.map((s) => <SaisineCard key={s.id} saisine={s} />)
            )}
          </div>

          {/* Pagination */}
          {totalFiltered > filters.pageSize && (
            <nav className="greffe-pagination" aria-label="Pagination du Greffe">
              <span className="greffe-page-info">
                {from}–{to} / {totalFiltered}
              </span>
              <div className="greffe-page-btns">
                <button
                  type="button"
                  className="greffe-page-btn"
                  onClick={() => setPage(Math.max(1, filters.page - 1))}
                  disabled={filters.page === 1}
                  aria-label="Page précédente"
                >
                  ‹
                </button>
                {pageButtons.map((p, i) =>
                  p === "…" ? (
                    <span
                      key={`gap-${i}`}
                      className="greffe-page-info"
                      style={{ alignSelf: "center", padding: "0 4px" }}
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`greffe-page-btn${p === filters.page ? " is-active" : ""}`}
                      onClick={() => setPage(p)}
                      aria-current={p === filters.page ? "page" : undefined}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  className="greffe-page-btn"
                  onClick={() => setPage(Math.min(totalPages, filters.page + 1))}
                  disabled={filters.page >= totalPages}
                  aria-label="Page suivante"
                >
                  ›
                </button>
              </div>
            </nav>
          )}
        </section>

        <div style={{ paddingTop: 48, textAlign: "right" }}>
          <Link to="/foyer" className="qv3-btn-link" style={{ color: "inherit" }}>
            ← Retour au Foyer
          </Link>
        </div>
      </main>

      <NanaeFooter variant="minimal" />
    </div>
  );
}

function buildPageList(current: number, total: number): Array<number | "…"> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: Array<number | "…"> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}
